import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Profile.css";
import Breadcrumb from "./Breadcrumb";
import { useMatter } from "../context/MatterContext";
import NoOpenMatterPage from "./NoOpenMatterPage";

const safeText = (value, fallback) => {
  const v = value === null || value === undefined ? "" : String(value).trim();
  return v ? v : fallback;
};

const formatDateBR = (iso) => {
  const raw = safeText(iso, "");
  if (!raw) return "(Não informado)";

  const parts = raw.split("-");
  if (parts.length !== 3) return "(Não informado)";

  const [year, month, day] = parts.map(Number);

  const d = new Date(year, month - 1, day);

  if (Number.isNaN(d.getTime())) return "(Não informado)";

  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatCpf = (cpf) => {
  const raw = safeText(cpf, "");
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 11) return raw ? raw : "(Não informado)";
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatPhone = (phone) => {
  const raw = safeText(phone, "");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "(Não informado)";
  return raw;
};

const formatAddress = (addr) => {
  if (!addr) return "(Nenhum endereço registrado)";

  if (typeof addr === "string") {
    const raw = safeText(addr, "");
    return raw || "(Nenhum endereço registrado)";
  }

  if (typeof addr !== "object") return "(Nenhum endereço registrado)";

  const street = safeText(addr.street, "");
  const city = safeText(addr.city, "");
  const state = safeText(addr.state, "");
  const postalCode = safeText(addr.postalCode, "");
  const country = safeText(addr.country, "");

  const line1 = [street].filter(Boolean).join("");
  const line2 = [city, state].filter(Boolean).join(" - ");
  const line3 = [postalCode, country].filter(Boolean).join(" · ");

  const lines = [line1, line2, line3].filter(Boolean);
  return lines.length ? lines.join("\n") : "(Nenhum endereço registrado)";
};

export default function Profile() {
  const navigate = useNavigate();
  const {
    party,
    matter,
    coCounsel,
    bootstrap,
    bootstrapping,
    bootstrapState,
  } = useMatter();

  useEffect(() => {
    const run = async () => {
      if (bootstrapState === "no_open_matter") return;
      if (party && matter) return;

      const result = await bootstrap?.();
      if (!result?.ok) {
        if (result?.reason === "no_open_matter") {
          return;
        }
        navigate("/signin", { replace: true });
        return;
      }

      navigate("/multimatters", { replace: true });
    };

    run();
  }, [party, matter, bootstrapState, bootstrap, navigate]);

  const partySafe = useMemo(
    () => (party && typeof party === "object" ? party : null),
    [party],
  );

  if (bootstrapState === "no_open_matter") {
    return <NoOpenMatterPage />;
  }

  if (!partySafe) {
    return (
      <div className="main-wrapper">
        <Breadcrumb
          items={[{ label: "Página Inicial", link: "/" }, { label: "Perfil" }]}
        />

        <div className="profilePage">
          <section className="profileCard">
            <div className="profileHeader">
              <h1 className="profileTitle">Perfil</h1>
              <p className="profileSubtitle">
                {bootstrapping
                  ? "Carregando seus dados…"
                  : "Não foi possível carregar seus dados."}
              </p>
            </div>

            <div className="profileEmpty">
              <div className="profileEmptyTitle">
                {bootstrapping ? "Carregando…" : "Dados indisponíveis"}
              </div>
              <div className="profileEmptySub">
                {bootstrapping
                  ? "Aguarde um momento."
                  : "Volte para a seleção de caso e tente novamente."}
              </div>

              <button
                type="button"
                className="profilePrimaryBtn"
                onClick={() => navigate("/multimatters")}
                disabled={bootstrapping}
              >
                Voltar
              </button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  const name = safeText(partySafe.name, "Cliente");
  const cpf = formatCpf(partySafe.cpf);
  const address = formatAddress(partySafe.billingAddress);
  const dob = formatDateBR(partySafe.dateOfBirth);

  const email = safeText(partySafe.email, "(Nenhum email registrado)");
  const phone = formatPhone(partySafe.phone);
  const pixKey = safeText(
    partySafe.bhprPixKey,
    "(Nenhuma chave PIX registrada)",
  );
  const coCounselSafe =
    coCounsel && typeof coCounsel === "object" ? coCounsel : null;
  const coCounselBarNumber = safeText(
    coCounselSafe?.barAssociationNumber,
    "(Não informado)",
  );
  const coCounselBarState = safeText(
    coCounselSafe?.barAssociationState,
    "(Não informado)",
  );
  const coCounselBarDisplay =
    coCounselBarNumber === "(Não informado)" &&
    coCounselBarState === "(Não informado)"
      ? "(Não informado)"
      : [coCounselBarNumber, coCounselBarState]
          .filter((value) => value !== "(Não informado)")
          .join(" - ");
  const coCounselAddress = formatAddress(
    coCounselSafe?.address
      ? {
          ...coCounselSafe.address,
          postalCode:
            coCounselSafe.address.postalCode || coCounselSafe.address.postcode,
        }
      : null,
  );

  return (
    <div className="main-wrapper">
      <Breadcrumb
        items={[{ label: "Página Inicial", link: "/" }, { label: "Perfil" }]}
      />

      <div className="profilePage">
        <section className="profileCard">
          <div className="profileHeader">
            <h1 className="profileTitle">Detalhes pessoais</h1>
          </div>

          <div className="profileSection">
            <div className="profileField">
              <div className="profileLabel">Nome</div>
              <div className="profileValue">{name}</div>
            </div>

            <div className="profileField">
              <div className="profileLabel">Endereço</div>
              <div className="profileValue" style={{ whiteSpace: "pre-line" }}>
                {address}
              </div>
            </div>

            <div className="profileField">
              <div className="profileLabel">Data de nascimento</div>
              <div className="profileValue">{dob}</div>
            </div>

            <div className="profileField">
              <div className="profileLabel">CPF</div>
              <div className="profileValue">{cpf}</div>
            </div>

          </div>

          <div className="profileDivider" />

          <div className="profileHeader profileHeaderTight">
            <h2 className="profileTitle2">Informações de contato</h2>
          </div>

          <div className="profileSection">
            <div className="profileField">
              <div className="profileLabel">Email</div>
              <div className="profileValue">{email}</div>
            </div>

            <div className="profileField">
              <div className="profileLabel">Número de Telefone</div>
              <div className="profileValue">{phone}</div>
            </div>

            <div className="profileField">
              <div className="profileLabel">Chave PIX</div>
              <div className="profileValue">{pixKey}</div>
            </div>
          </div>

          <div className="profileDivider" />

          <div className="profileHeader profileHeaderTight">
            <h2 className="profileTitle2">Advogado corresponsável</h2>
          </div>

          <div className="profileSection">
            {coCounselSafe ? (
              <>
                <div className="profileField">
                  <div className="profileLabel">Nome</div>
                  <div className="profileValue">
                    {safeText(coCounselSafe.name, "(Não informado)")}
                  </div>
                </div>

                <div className="profileField">
                  <div className="profileLabel">Email</div>
                  <div className="profileValue">
                    {safeText(coCounselSafe.email, "(Não informado)")}
                  </div>
                </div>

                <div className="profileField">
                  <div className="profileLabel">Telefone</div>
                  <div className="profileValue">
                    {formatPhone(coCounselSafe.phone)}
                  </div>
                </div>

                <div className="profileField">
                  <div className="profileLabel">OAB</div>
                  <div className="profileValue">{coCounselBarDisplay}</div>
                </div>

                <div className="profileField">
                  <div className="profileLabel">Endereço</div>
                  <div className="profileValue" style={{ whiteSpace: "pre-line" }}>
                    {coCounselAddress}
                  </div>
                </div>
              </>
            ) : (
              <div className="profileField">
                <div className="profileValue">
                  Você não selecionou um advogado corresponsável.
                </div>
              </div>
            )}
          </div>

          <p className="profileFooterNote">
            Essas informações estão atualizadas? Nos informe caso haja alguma
            alteração.
          </p>

          <button
            type="button"
            className="profileLinkBtn"
            onClick={() => navigate("/contact-edit")}
          >
            Atualizar informações de contato
          </button>

          <button
            type="button"
            className="profileLinkBtn"
            onClick={() => navigate(-1)}
          >
            Voltar
          </button>
        </section>

        <div className="bottomSpacer" />
      </div>
    </div>
  );
}
